(() => {
  const galleryCache = new WeakMap();
  const setupCache = new WeakMap();
  const nativeGalleries = new WeakMap();
  const mobileGalleryQuery = window.matchMedia('(max-width: 989px), (hover: none), (pointer: coarse)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const responsiveWidths = [165, 360, 533, 720, 940, 1066];
  const controlSelectors = [
    '.custom-mobile-collection-gallery__product-link',
    '[data-custom-mobile-collection-gallery-status]',
    '.custom-mobile-collection-gallery__indicators',
    '.custom-mobile-collection-gallery__arrow--previous',
    '.custom-mobile-collection-gallery__arrow--next',
  ];

  const imageUrlAtWidth = (source, width) => {
    const url = new URL(source, window.location.href);
    url.searchParams.set('width', String(width));
    return url.href;
  };

  const readSetup = (setup) => {
    if (setupCache.has(setup)) return setupCache.get(setup);

    const items = Array.from(setup.content.querySelectorAll('[data-gallery-src]')).map((item) => ({
      src: item.dataset.gallerySrc,
      alt: item.dataset.galleryAlt,
      width: Number.parseInt(item.dataset.galleryWidth, 10),
      height: Number.parseInt(item.dataset.galleryHeight, 10),
    }));
    const value = { items };
    setupCache.set(setup, value);
    return value;
  };

  const getSrcset = (item) => {
    const widths = responsiveWidths.filter((width) => width < item.width);
    widths.push(item.width);
    return [...new Set(widths)].map((width) => `${imageUrlAtWidth(item.src, width)} ${width}w`).join(',');
  };

  const setImageAttributes = (image, item) => {
    image.srcset = getSrcset(item);
    image.src = imageUrlAtWidth(item.src, Math.min(533, item.width));
    image.alt = item.alt;
    image.width = item.width;
    image.height = item.height;
  };

  const initializeSetup = (setup) => {
    if (setup.dataset.galleryReady === 'true') return;

    const cardItem = setup.closest('.grid__item');
    const card = cardItem?.querySelector('.card-wrapper');
    const gallery = card?.querySelector('.card__media .media');
    const image = gallery?.querySelector(':scope > img');
    if (!card || !gallery || !image) return;

    const { items } = readSetup(setup);
    if (items.length === 0) return;

    setup.dataset.galleryReady = 'true';
    card.classList.add('custom-mobile-collection-gallery-card');
    gallery.setAttribute('data-custom-mobile-collection-gallery', '');
    gallery.dataset.galleryIndex = '0';
    image.classList.add('custom-mobile-collection-gallery__image');
    galleryCache.set(gallery, items);
    controlSelectors.forEach((selector) => {
      const control = setup.content.querySelector(selector);
      if (control) gallery.append(control.cloneNode(true));
    });
    if (mobileGalleryQuery.matches && items.length > 1) initializeNativeGallery(gallery, image, items);
  };

  const initializeAll = (root = document) => {
    if (root instanceof Element && root.matches('[data-custom-mobile-collection-gallery-setup]')) {
      initializeSetup(root);
    }
    root.querySelectorAll?.('[data-custom-mobile-collection-gallery-setup]').forEach(initializeSetup);
  };

  const updateIndicators = (gallery, index) => {
    const dots = gallery.querySelectorAll('[data-custom-mobile-collection-gallery-index]');
    dots.forEach((dot) => {
      const isActive = Number.parseInt(dot.dataset.customMobileCollectionGalleryIndex, 10) === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });

    const more = gallery.querySelector('[data-custom-mobile-collection-gallery-more]');
    if (more) more.classList.toggle('is-active', index >= dots.length);
  };

  const updateGalleryState = (gallery, index, itemCount) => {
    gallery.dataset.galleryIndex = String(index);
    const status = gallery.querySelector('[data-custom-mobile-collection-gallery-status]');
    if (status) status.textContent = `Image ${index + 1} of ${itemCount}`;
    updateIndicators(gallery, index);
  };

  const normalizeIndex = (index, count) => ((index % count) + count) % count;

  const initializeNativeGallery = (gallery, originalImage, items) => {
    if (nativeGalleries.has(gallery)) return;
    const productLink = gallery.querySelector(':scope > .custom-mobile-collection-gallery__product-link');
    if (!productLink) return;
    const initialIndex = Number.parseInt(gallery.dataset.galleryIndex || '0', 10);
    const track = document.createElement('div');
    track.className = 'custom-mobile-collection-gallery__native-track';
    track.style.visibility = 'hidden';
    const state = {
      gallery, originalImage, items, track, slides: [], width: 0,
      index: initialIndex, lastGood: initialIndex, recent: [initialIndex],
      touching: false, positioned: false, destroyed: false, timers: new Set(),
    };
    nativeGalleries.set(gallery, state);

    const syncSelection = () => {
      if (!state.width || state.destroyed) return;
      const position = Math.max(0, Math.min(items.length + 1, Math.round(track.scrollLeft / state.width)));
      const index = state.slides[position].index;
      if (index !== state.index) {
        state.index = index;
        state.recent = [index, ...state.recent.filter((value) => value !== index)].slice(0, 3);
        updateGalleryState(gallery, index, items.length);
      }
      if (state.accessibilityIndex !== index) {
        state.accessibilityIndex = index;
        state.slides.forEach((slide, position) => {
          const selected = position === index + 1;
          slide.anchor.tabIndex = selected ? 0 : -1;
          slide.anchor.setAttribute('aria-hidden', String(!selected));
        });
      }
    };

    const loadSlide = (slide, initial = false) => {
      if (slide.image || state.destroyed) return;
      slide.failed = false;
      const viewPosition = state.width ? track.scrollLeft / state.width : initialIndex + 1;
      const cached = state.slides.find((other, position) =>
        other !== slide && other.index === slide.index && other.image?.complete &&
        other.image.naturalWidth > 0 && Math.abs(position - viewPosition) >= 1 &&
        new URL(other.image.src, location.href).pathname === new URL(items[slide.index].src, location.href).pathname
      );
      if (cached) {
        slide.image = cached.image;
        cached.image = null;
        slide.anchor.append(slide.image);
        return;
      }
      const image = originalImage.cloneNode(false);
      image.className = 'custom-mobile-collection-gallery__native-photo';
      image.removeAttribute('id');
      image.style.removeProperty('--gallery-x');
      image.removeAttribute('aria-hidden');
      image.draggable = false;
      if (!initial) {
        image.loading = 'eager';
        setImageAttributes(image, items[slide.index]);
      }
      slide.image = image;
      let done = false;
      let timer;
      const finish = (loaded) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        state.timers.delete(timer);
        if (state.destroyed) return;
        if (loaded) {
          if (state.index === slide.index) {
            state.lastGood = slide.index;
            if (!state.touching) state.mirrorForCardConsumers?.(image);
          }
        } else {
          const holder = state.slides.find((entry) => entry.image === image) || slide;
          image.remove();
          holder.image = null;
          holder.failed = true;
          if (state.index === holder.index && !state.touching) goNative(state, state.lastGood, true);
        }
      };
      image.addEventListener('load', () => finish(true), { once: true });
      image.addEventListener('error', () => finish(false), { once: true });
      slide.anchor.append(image);
      if (!initial) {
        timer = setTimeout(() => finish(false), 8000);
        state.timers.add(timer);
      }
      if (image.complete && image.naturalWidth > 0) finish(true);
    };
    state.loadSlide = loadSlide;

    // End copies preserve the existing first/last wrap. Empty slides have no image URL.
    const indexes = [items.length - 1, ...items.map((_, index) => index), 0];
    indexes.forEach((index, position) => {
      const anchor = productLink.cloneNode(false);
      anchor.classList.add('custom-mobile-collection-gallery__native-slide');
      anchor.removeAttribute('id');
      anchor.draggable = false;
      anchor.tabIndex = -1;
      anchor.setAttribute('aria-hidden', 'true');
      anchor.dataset.gallerySlide = String(index);
      const slide = { anchor, index, image: null };
      state.slides.push(slide);
      track.append(anchor);
      if (position === initialIndex + 1) loadSlide(slide, true);
    });
    gallery.append(track);

    const mirrorForCardConsumers = (photo) => {
      if (!photo) return;
      // Existing Saved Items/card consumers read the original first img. It is hidden
      // on mobile; keep its data aligned without replacing the visible native photo.
      if (originalImage.srcset !== photo.srcset) originalImage.srcset = photo.srcset;
      if (originalImage.src !== photo.src) originalImage.src = photo.src;
      originalImage.alt = photo.alt;
      originalImage.width = photo.width;
      originalImage.height = photo.height;
    };
    state.mirrorForCardConsumers = mirrorForCardConsumers;

    const settle = () => {
      if (state.touching || state.destroyed || !state.width) return;
      syncSelection();
      const position = Math.max(0, Math.min(items.length + 1, Math.round(track.scrollLeft / state.width)));
      if (Math.abs(track.scrollLeft - position * state.width) > 1) return;
      if (state.slides[position].failed) {
        goNative(state, state.lastGood, true);
        return;
      }
      state.target = null;
      if (position === 0 || position === items.length + 1) {
        const buffer = state.slides[position];
        const actual = state.slides[state.index + 1];
        // Move the same decoded photo to its real position before an invisible wrap.
        if (buffer.image) {
          actual.image?.remove();
          actual.image = buffer.image;
          actual.anchor.append(buffer.image);
          buffer.image = null;
        }
        loadSlide(actual);
        track.style.scrollSnapType = 'none';
        track.scrollLeft = (state.index + 1) * state.width;
        requestAnimationFrame(() => { if (!state.destroyed) track.style.scrollSnapType = ''; });
      }
      state.slides.forEach((slide) => {
        // Keep only three visited photo choices; never fetch an idle neighboring photo.
        if (slide.image && !state.recent.includes(slide.index)) {
          slide.image.remove();
          slide.image = null;
        }
      });
      const currentPhoto = state.slides[state.index + 1].image;
      if (currentPhoto?.complete && currentPhoto.naturalWidth > 0) {
        state.lastGood = state.index;
        mirrorForCardConsumers(currentPhoto);
      }
    };
    const queueSettle = () => {
      clearTimeout(state.settleTimer);
      state.settleTimer = setTimeout(settle, 120);
    };
    track.addEventListener('scroll', () => {
      if (!state.positioned) return;
      syncSelection();
      queueSettle();
    }, { passive: true });
    track.addEventListener('scrollend', settle, { passive: true });
    track.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      clearTimeout(state.settleTimer);
      state.touching = true;
      state.target = null;
      state.touchX = event.touches[0].clientX;
      state.touchY = event.touches[0].clientY;
      state.horizontal = false;
    }, { passive: true });
    track.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const dx = Math.abs(event.touches[0].clientX - state.touchX);
      const dy = Math.abs(event.touches[0].clientY - state.touchY);
      if (dx > 8 && dx > dy * 1.2) {
        state.horizontal = true;
        gallery.dataset.gallerySwipeAt = String(Date.now());
      }
      // The browser moves and decelerates the track; JavaScript never sets its drag position.
    }, { passive: true });
    const endTouch = () => {
      state.touching = false;
      if (state.horizontal) gallery.dataset.gallerySwipeAt = String(Date.now());
      queueSettle();
    };
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', endTouch, { passive: true });

    state.intersections = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.01) return;
        const slide = state.slides.find((slide) => slide.anchor === entry.target);
        if (slide) loadSlide(slide);
      });
    }, { root: track, threshold: [0.01] });
    state.resize = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (!width || Math.abs(width - state.width) < 0.1) return;
      state.width = width;
      track.style.scrollSnapType = 'none';
      track.scrollLeft = (state.index + 1) * width;
      state.positioned = true;
      gallery.classList.add('is-native-gallery');
      track.style.visibility = '';
      requestAnimationFrame(() => {
        if (state.destroyed) return;
        track.style.scrollSnapType = '';
        state.slides.forEach((slide) => state.intersections.observe(slide.anchor));
        syncSelection();
      });
    });
    state.resize.observe(track);
  };

  const goNative = (state, index, instant = false) => {
    if (state.destroyed) return;
    const selected = normalizeIndex(index, state.items.length);
    state.target = selected;
    state.loadSlide(state.slides[selected + 1]);
    state.track.scrollTo({
      left: (selected + 1) * state.width,
      behavior: instant || reducedMotionQuery.matches ? 'instant' : 'smooth',
    });
  };

  const destroyNativeGallery = (gallery, restoreDesktop = false) => {
    const state = nativeGalleries.get(gallery);
    if (!state) return;
    state.destroyed = true;
    clearTimeout(state.settleTimer);
    state.timers.forEach(clearTimeout);
    state.intersections.disconnect();
    state.resize.disconnect();
    if (restoreDesktop) {
      const selected = state.slides[state.index + 1].image ||
        state.slides.find((slide) => slide.index === state.index && slide.image)?.image;
      if (selected) state.mirrorForCardConsumers(selected);
      else setImageAttributes(state.originalImage, state.items[state.index]);
    }
    state.track.remove();
    gallery.classList.remove('is-native-gallery');
    nativeGalleries.delete(gallery);
  };

  const showImageAt = (gallery, nextIndex) => {
    const items = galleryCache.get(gallery) || [];
    if (items.length < 2) return;
    const next = normalizeIndex(nextIndex, items.length);
    const native = nativeGalleries.get(gallery);
    if (native && mobileGalleryQuery.matches) {
      goNative(native, next);
      return;
    }
    const image = gallery.querySelector(':scope > .custom-mobile-collection-gallery__image');
    if (!image || next === Number.parseInt(gallery.dataset.galleryIndex || '0', 10)) return;
    setImageAttributes(image, items[next]);
    updateGalleryState(gallery, next, items.length);
  };

  const showImage = (gallery, direction) => {
    const native = nativeGalleries.get(gallery);
    const current = native?.target ?? Number.parseInt(gallery.dataset.galleryIndex || '0', 10);
    showImageAt(gallery, current + (direction === 'previous' ? -1 : 1));
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const dot = event.target.closest('[data-custom-mobile-collection-gallery-index]');
    if (dot) {
      const gallery = dot.closest('[data-custom-mobile-collection-gallery]');
      if (!gallery) return;
      event.preventDefault();
      event.stopPropagation();
      showImageAt(gallery, Number.parseInt(dot.dataset.customMobileCollectionGalleryIndex, 10));
      return;
    }

    const arrow = event.target.closest('[data-custom-mobile-collection-gallery-direction]');
    if (arrow) {
      const gallery = arrow.closest('[data-custom-mobile-collection-gallery]');
      if (!gallery) return;
      event.preventDefault();
      event.stopPropagation();
      showImage(gallery, arrow.dataset.customMobileCollectionGalleryDirection);
      return;
    }

    const productLink = event.target.closest('.custom-mobile-collection-gallery__product-link');
    const gallery = productLink?.closest('[data-custom-mobile-collection-gallery]');
    if (gallery && Number.parseInt(gallery.dataset.gallerySwipeAt || '0', 10) > Date.now() - 500) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  mobileGalleryQuery.addEventListener('change', () => {
    document.querySelectorAll('[data-custom-mobile-collection-gallery]').forEach((gallery) => {
      if (!mobileGalleryQuery.matches) {
        destroyNativeGallery(gallery, true);
        return;
      }
      const items = galleryCache.get(gallery) || [];
      const image = gallery.querySelector(':scope > .custom-mobile-collection-gallery__image');
      if (image && items.length > 1) initializeNativeGallery(gallery, image, items);
    });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeAll(node);
      });
      mutation.removedNodes.forEach((node) => {
        if (!(node instanceof Element) || node.isConnected) return;
        if (node.matches('[data-custom-mobile-collection-gallery]')) destroyNativeGallery(node);
        node.querySelectorAll('[data-custom-mobile-collection-gallery]').forEach((gallery) => destroyNativeGallery(gallery));
      });
    });
  });

  initializeAll();
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
